import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { USE_MOCK_AUTH } from '../config/auth'
import { Auth0AuthProvider, useAuth0Context } from './Auth0Context'
import { BackendAuthProvider, useBackendAuth } from './BackendAuthContext'

interface User {
  id: string
  name: string
  email: string
  picture?: string
  username?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  logout: () => void
  getAccessToken: () => Promise<string | undefined>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Mock user data for development
const MOCK_USERS = [
  {
    id: 'mock-user-1',
    name: 'John Developer',
    email: 'john@example.com',
    username: 'johndev',
    picture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
  },
  {
    id: 'mock-user-2',
    name: 'Jane Coder',
    email: 'jane@example.com',
    username: 'janecoder',
    picture: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
  },
  {
    id: 'mock-user-3',
    name: 'Alex Programmer',
    email: 'alex@example.com',
    username: 'alexprog',
    picture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
  },
]

// Mock Auth Provider for development
function MockAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Check if user is logged in from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem('mock-auth-user')
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser))
      } catch (error) {
        console.error('Error parsing saved user:', error)
        localStorage.removeItem('mock-auth-user')
      }
    }
  }, [])

  const login = () => {
    setIsLoading(true)
    
    // Simulate login delay
    setTimeout(() => {
      // Randomly select a mock user
      const randomUser = MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)]
      if (randomUser) {
        setUser(randomUser)
        localStorage.setItem('mock-auth-user', JSON.stringify(randomUser))
      }
      setIsLoading(false)
    }, 1000)
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('mock-auth-user')
  }

  const getAccessToken = async (): Promise<string | undefined> => {
    // Return a mock token for development
    return 'mock-access-token-for-development'
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Main AuthProvider that chooses between mock and backend auth
export function AuthProvider({ children }: { children: ReactNode }) {
  if (USE_MOCK_AUTH) {
    return <MockAuthProvider>{children}</MockAuthProvider>
  } else {
    // Use backend auth proxy for Regular Web Application
    return <BackendAuthProvider>{children}</BackendAuthProvider>
  }
}

export function useAuth() {
  if (USE_MOCK_AUTH) {
    const context = useContext(AuthContext)
    if (context === undefined) {
      throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
  } else {
    // Use backend auth proxy
    return useBackendAuth()
  }
}

// Export mock users for testing
export { MOCK_USERS } 